import { format } from 'date-fns';
import { api, apiPublic } from '@/services/api';
import axios from 'axios';

// Configuração base do axios
const apiClient = axios.create({
  baseURL: 'https://api.example.com',
});

// Adicionar token de autenticação se disponível
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    if (!config.headers) {
      config.headers = {};
    }
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Obtém estatísticas de uma partida
 */
export const getMatchStats = async (matchId: string, isAuthenticated: boolean) => {
  try {
    const apiClient = isAuthenticated ? api : apiPublic;
    const response = await apiClient.get(`/football/getMatchStats/${matchId}`);
    const data = response.data.body?.data || response.data?.data || [];

    // Garantir que retornamos um array
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Erro ao buscar estatísticas:', error);

    // Gerar valores aleatórios mais realistas para demonstração
    const homeTeamDominance = Math.random(); // 0-1, quanto maior, mais dominante o time da casa

    // Posse de bola - baseada na dominância do time da casa
    const homePossession = Math.floor(40 + homeTeamDominance * 30); // 40-70%
    const awayPossession = 100 - homePossession;

    // Finalizações - times dominantes tendem a finalizar mais
    const homeShots = Math.floor(5 + homeTeamDominance * 15); // 5-20 chutes
    const awayShots = Math.floor(5 + (1 - homeTeamDominance) * 15);

    // Finalizações no alvo - geralmente 30-70% das finalizações são no alvo
    const homeShotsOnTarget = Math.floor(homeShots * (0.3 + Math.random() * 0.4));
    const awayShotsOnTarget = Math.floor(awayShots * (0.3 + Math.random() * 0.4));

    // Ataques - times dominantes têm mais ataques
    const homeAttacks = Math.floor(20 + homeTeamDominance * 30); // 20-50 ataques
    const awayAttacks = Math.floor(20 + (1 - homeTeamDominance) * 30);

    // Ataques perigosos - geralmente 30-60% dos ataques são perigosos
    const homeDangerousAttacks = Math.floor(homeAttacks * (0.3 + Math.random() * 0.3));
    const awayDangerousAttacks = Math.floor(awayAttacks * (0.3 + Math.random() * 0.3));

    // Passes - times com mais posse fazem mais passes
    const homePasses = Math.floor(200 + homeTeamDominance * 300); // 200-500 passes
    const awayPasses = Math.floor(200 + (1 - homeTeamDominance) * 300);

    // Precisão dos passes - geralmente entre 70-90%
    const homePassAccuracy = Math.floor(70 + homeTeamDominance * 20);
    const awayPassAccuracy = Math.floor(70 + (1 - homeTeamDominance) * 20);

    // Passes precisos - calculados com base na precisão
    const homeAccuratePasses = Math.floor(homePasses * (homePassAccuracy / 100));
    const awayAccuratePasses = Math.floor(awayPasses * (awayPassAccuracy / 100));

    // Escanteios - times dominantes tendem a ter mais escanteios
    const homeCorners = Math.floor(2 + homeTeamDominance * 8); // 2-10 escanteios
    const awayCorners = Math.floor(2 + (1 - homeTeamDominance) * 8);

    // Faltas - distribuição mais aleatória, times menos dominantes tendem a fazer mais faltas
    const homeFouls = Math.floor(8 + (1 - homeTeamDominance) * 8); // 8-16 faltas
    const awayFouls = Math.floor(8 + homeTeamDominance * 8);

    // Impedimentos - relacionados com o estilo de jogo ofensivo
    const homeOffsides = Math.floor(1 + homeTeamDominance * 5); // 1-6 impedimentos
    const awayOffsides = Math.floor(1 + (1 - homeTeamDominance) * 5);

    // Cartões amarelos - relacionados com faltas e estilo de jogo
    const homeYellowCards = Math.min(Math.floor(homeFouls * 0.3), 5); // Máximo de 5 cartões
    const awayYellowCards = Math.min(Math.floor(awayFouls * 0.3), 5);

    // Cartões vermelhos - raros, mas possíveis
    const homeRedCards = Math.random() > 0.9 ? 1 : 0;
    const awayRedCards = Math.random() > 0.9 ? 1 : 0;

    // Defesas do goleiro - relacionadas com finalizações no alvo do adversário
    const homeGoalkeeperSaves = Math.floor(awayShotsOnTarget * 0.7); // 70% das finalizações no alvo são defendidas
    const awayGoalkeeperSaves = Math.floor(homeShotsOnTarget * 0.7);

    // Chutes bloqueados - parte das finalizações que não são no alvo
    const homeBlockedShots = Math.floor((homeShots - homeShotsOnTarget) * 0.4); // 40% dos chutes fora do alvo são bloqueados
    const awayBlockedShots = Math.floor((awayShots - awayShotsOnTarget) * 0.4);

    // Chutes dentro e fora da área
    const homeInsideBoxShots = Math.floor(homeShots * 0.7); // 70% dos chutes são de dentro da área
    const homeOutsideBoxShots = homeShots - homeInsideBoxShots;
    const awayInsideBoxShots = Math.floor(awayShots * 0.7);
    const awayOutsideBoxShots = awayShots - awayInsideBoxShots;

    // Dados simulados para desenvolvimento
    return [
      { type: "Ball Possession", home: homePossession.toString(), away: awayPossession.toString() },
      { type: "Shots Total", home: homeShots.toString(), away: awayShots.toString() },
      { type: "Shots On Goal", home: homeShotsOnTarget.toString(), away: awayShotsOnTarget.toString() },
      { type: "Shots Off Goal", home: (homeShots - homeShotsOnTarget - homeBlockedShots).toString(), away: (awayShots - awayShotsOnTarget - awayBlockedShots).toString() },
      { type: "Shots Blocked", home: homeBlockedShots.toString(), away: awayBlockedShots.toString() },
      { type: "Shots Inside Box", home: homeInsideBoxShots.toString(), away: awayInsideBoxShots.toString() },
      { type: "Shots Outside Box", home: homeOutsideBoxShots.toString(), away: awayOutsideBoxShots.toString() },
      { type: "Fouls", home: homeFouls.toString(), away: awayFouls.toString() },
      { type: "Corner Kicks", home: homeCorners.toString(), away: awayCorners.toString() },
      { type: "Offsides", home: homeOffsides.toString(), away: awayOffsides.toString() },
      { type: "Yellow Cards", home: homeYellowCards.toString(), away: awayYellowCards.toString() },
      { type: "Red Cards", home: homeRedCards.toString(), away: awayRedCards.toString() },
      { type: "Goalkeeper Saves", home: homeGoalkeeperSaves.toString(), away: awayGoalkeeperSaves.toString() },
      { type: "Total Passes", home: homePasses.toString(), away: awayPasses.toString() },
      { type: "Passes Accurate", home: homeAccuratePasses.toString(), away: awayAccuratePasses.toString() },
      { type: "Passes %", home: homePassAccuracy.toString(), away: awayPassAccuracy.toString() },
      { type: "Attacks", home: homeAttacks.toString(), away: awayAttacks.toString() },
      { type: "Dangerous Attacks", home: homeDangerousAttacks.toString(), away: awayDangerousAttacks.toString() },
      { type: "Expected Goals (xG)", home: (homeShotsOnTarget * 0.1 + homeInsideBoxShots * 0.05).toFixed(2), away: (awayShotsOnTarget * 0.1 + awayInsideBoxShots * 0.05).toFixed(2) }
    ];
  }
};

/**
 * Busca as odds ao vivo de uma partida específica
 * @param matchId ID da partida
 * @param isAuthenticated Se o usuário está autenticado
 * @returns Dados das odds ao vivo
 */
export const getLiveOddsByMatch = async (matchId: string, isAuthenticated: boolean) => {
  try {
    const apiClient = isAuthenticated ? api : apiPublic;
    const response = await apiClient.get(`/football/getLiveOdds/${matchId}`);
    return response.data.body?.data || response.data?.data || [];
  } catch (error) {
    console.error('Erro ao buscar odds:', error);
    throw error;
  }
};

/**
 * Carregar partidas ao vivo da API
 */
export const fetchLiveMatches = async (isAuthenticated: boolean) => {
  try {
    const apiClient = isAuthenticated ? api : apiPublic;
    const response = await apiClient.get('/football/getLiveMatches');
    return response.data.body?.data || response.data?.data || [];
  } catch (error) {
    console.error('Erro ao carregar partidas ao vivo:', error);
    return [];
  }
};

/**
 * Carregar partidas históricas da API
 */
export const fetchHistoricalMatches = async (isAuthenticated: boolean, date: Date) => {
  try {
    const apiClient = isAuthenticated ? api : apiPublic;
    const formattedDate = format(date, 'yyyy-MM-dd');
    const response = await apiClient.get('/football/getHistMatches', {
      params: {
        from: formattedDate,
        to: formattedDate,
        pageSize: 40,
        page: 1
      }
    });
    return response.data.body?.data || response.data?.data || [];
  } catch (error) {
    console.error('Erro ao carregar partidas históricas:', error);
    return [];
  }
};

/**
 * Carregar partidas históricas da API (versão gratuita)
 */
export const fetchHistoricalMatchesFree = async (isAuthenticated: boolean, date: Date) => {
  try {
    const apiClient = isAuthenticated ? api : apiPublic;
    const formattedDate = format(date, 'yyyy-MM-dd');
    const response = await apiClient.get('/football/getHistMatchesFree', {
      params: {
        from: formattedDate,
        to: formattedDate,
        pageSize: 40,
        page: 1
      }
    });
    return response.data.body?.data || response.data?.data || [];
  } catch (error) {
    console.error('Erro ao carregar partidas históricas (versão gratuita):', error);
    return [];
  }
};

// Função para buscar eventos da partida (gols, cartões, etc.)
export const getMatchEvents = async (matchId: string, isAuthenticated: boolean) => {
  try {
    const apiClient = isAuthenticated ? api : apiPublic;
    const response = await apiClient.get(`/football/getMatchEvents/${matchId}`);
    return response.data.body?.data || response.data?.data || {
      goalscorer: [],
      cards: []
    };
  } catch (error) {
    console.error('Erro ao buscar eventos:', error);

    // Buscar estatísticas para gerar eventos mais realistas
    let statsData;
    try {
      statsData = await getMatchStats(matchId, isAuthenticated);
    } catch (e) {
      console.error('Erro ao buscar estatísticas para eventos:', e);
      statsData = null;
    }

    // Extrair estatísticas relevantes para gerar eventos realistas
    const shots = statsData?.find((stat: any) => stat.type === "Shots Total");
    const homeShots = shots ? parseInt(shots.home) : 10;
    const awayShots = shots ? parseInt(shots.away) : 8;

    const shotsOnGoal = statsData?.find((stat: any) => stat.type === "Shots On Goal");
    const homeShotsOnGoal = shotsOnGoal ? parseInt(shotsOnGoal.home) : Math.floor(homeShots * 0.4);
    const awayShotsOnGoal = shotsOnGoal ? parseInt(shotsOnGoal.away) : Math.floor(awayShots * 0.4);

    const yellowCards = statsData?.find((stat: any) => stat.type === "Yellow Cards");
    const homeYellowCards = yellowCards ? parseInt(yellowCards.home) : Math.floor(Math.random() * 4);
    const awayYellowCards = yellowCards ? parseInt(yellowCards.away) : Math.floor(Math.random() * 4);

    const redCards = statsData?.find((stat: any) => stat.type === "Red Cards");
    const homeRedCards = redCards ? parseInt(redCards.home) : (Math.random() > 0.9 ? 1 : 0);
    const awayRedCards = redCards ? parseInt(redCards.away) : (Math.random() > 0.9 ? 1 : 0);

    // Gerar gols com base nas finalizações no alvo
    // Em média, 30% das finalizações no alvo resultam em gol
    const homeGoals = Math.min(Math.floor(homeShotsOnGoal * 0.3), 5); // Máximo de 5 gols
    const awayGoals = Math.min(Math.floor(awayShotsOnGoal * 0.3), 5);

    // Nomes de jogadores mais realistas para times brasileiros
    const homePlayerNames = [
      "Gabriel Silva", "Lucas Oliveira", "Matheus Santos", "Pedro Alves", "João Costa",
      "Rafael Souza", "Bruno Ferreira", "Thiago Lima", "Gustavo Pereira", "Marcelo Rodrigues",
      "Felipe Almeida", "Diego Martins", "André Gomes", "Leandro Carvalho", "Vitor Ribeiro"
    ];

    const awayPlayerNames = [
      "Carlos Eduardo", "Marcos Paulo", "Luiz Henrique", "Roberto Carlos", "Paulo César",
      "Júlio César", "Ronaldo Nazário", "Adriano Imperador", "Neymar Júnior", "Alexandre Pato",
      "Robinho Silva", "Kaká Ricardo", "Ronaldinho Gaúcho", "Romário Faria", "Rivaldo Vítor"
    ];

    // Função para obter um nome aleatório de jogador
    const getRandomHomePlayer = () => homePlayerNames[Math.floor(Math.random() * homePlayerNames.length)];
    const getRandomAwayPlayer = () => awayPlayerNames[Math.floor(Math.random() * awayPlayerNames.length)];

    // Gerar minutos para os eventos de forma realista
    // Distribuir os eventos ao longo dos 90 minutos, com maior probabilidade em certos períodos
    const generateMinute = () => {
      const period = Math.random();
      if (period < 0.1) {
        // Primeiros 15 minutos (10% dos eventos)
        return Math.floor(1 + Math.random() * 15);
      } else if (period < 0.25) {
        // 15-30 minutos (15% dos eventos)
        return Math.floor(15 + Math.random() * 15);
      } else if (period < 0.35) {
        // 30-45 minutos (10% dos eventos)
        return Math.floor(30 + Math.random() * 15);
      } else if (period < 0.45) {
        // 45-60 minutos (10% dos eventos)
        return Math.floor(45 + Math.random() * 15);
      } else if (period < 0.7) {
        // 60-75 minutos (25% dos eventos)
        return Math.floor(60 + Math.random() * 15);
      } else {
        // 75-90 minutos (30% dos eventos)
        return Math.floor(75 + Math.random() * 15);
      }
    };

    const goalscorers = [];
    const cards = [];
    let currentHomeScore = 0;
    let currentAwayScore = 0;

    // Gerar gols para o time da casa
    for (let i = 0; i < homeGoals; i++) {
      const minute = generateMinute();
      const scorer = getRandomHomePlayer();

      // 60% de chance de ter assistência
      const hasAssist = Math.random() < 0.6;
      const assist = hasAssist ? getRandomHomePlayer() : "";

      // Evitar que o assistente seja o mesmo que o goleador
      const finalAssist = assist === scorer ? "" : assist;

      currentHomeScore++;

      goalscorers.push({
        time: minute.toString(),
        home_scorer: scorer,
        home_scorer_id: (100 + i).toString(),
        home_assist: finalAssist,
        home_assist_id: finalAssist ? (200 + i).toString() : "",
        score: `${currentHomeScore}-${currentAwayScore}`,
        away_scorer: "",
        away_scorer_id: "",
        away_assist: "",
        away_assist_id: ""
      });
    }

    // Gerar gols para o time visitante
    for (let i = 0; i < awayGoals; i++) {
      const minute = generateMinute();
      const scorer = getRandomAwayPlayer();

      // 60% de chance de ter assistência
      const hasAssist = Math.random() < 0.6;
      const assist = hasAssist ? getRandomAwayPlayer() : "";

      // Evitar que o assistente seja o mesmo que o goleador
      const finalAssist = assist === scorer ? "" : assist;

      currentAwayScore++;

      goalscorers.push({
        time: minute.toString(),
        away_scorer: scorer,
        away_scorer_id: (300 + i).toString(),
        away_assist: finalAssist,
        away_assist_id: finalAssist ? (400 + i).toString() : "",
        score: `${currentHomeScore}-${currentAwayScore}`,
        home_scorer: "",
        home_scorer_id: "",
        home_assist: "",
        home_assist_id: ""
      });
    }

    // Gerar cartões amarelos para o time da casa
    for (let i = 0; i < homeYellowCards; i++) {
      const minute = generateMinute();
      const player = getRandomHomePlayer();

      cards.push({
        time: minute.toString(),
        home_fault: player,
        card: "yellow card",
        away_fault: "",
        info: Math.random() < 0.5 ? "Falta dura" : Math.random() < 0.5 ? "Reclamação" : "Retardar o reinício"
      });
    }

    // Gerar cartões amarelos para o time visitante
    for (let i = 0; i < awayYellowCards; i++) {
      const minute = generateMinute();
      const player = getRandomAwayPlayer();

      cards.push({
        time: minute.toString(),
        away_fault: player,
        card: "yellow card",
        home_fault: "",
        info: Math.random() < 0.5 ? "Falta dura" : Math.random() < 0.5 ? "Reclamação" : "Retardar o reinício"
      });
    }

    // Gerar cartões vermelhos para o time da casa
    for (let i = 0; i < homeRedCards; i++) {
      // 70% de chance de ser segundo amarelo, 30% de ser vermelho direto
      const isSecondYellow = Math.random() < 0.7;
      const minute = generateMinute();

      // Tentar usar um jogador que já recebeu amarelo
      let player = "";
      const yellowCardPlayers = cards
        .filter(card => card.home_fault && card.card === "yellow card")
        .map(card => card.home_fault);

      if (isSecondYellow && yellowCardPlayers.length > 0) {
        // Usar um jogador que já recebeu amarelo
        player = yellowCardPlayers[Math.floor(Math.random() * yellowCardPlayers.length)];
      } else {
        // Usar um jogador aleatório
        player = getRandomHomePlayer();
      }

      cards.push({
        time: minute.toString(),
        home_fault: player,
        card: isSecondYellow ? "yellow card/red card" : "red card",
        away_fault: "",
        info: isSecondYellow ? "Segundo cartão amarelo" : "Falta grave"
      });
    }

    // Gerar cartões vermelhos para o time visitante
    for (let i = 0; i < awayRedCards; i++) {
      // 70% de chance de ser segundo amarelo, 30% de ser vermelho direto
      const isSecondYellow = Math.random() < 0.7;
      const minute = generateMinute();

      // Tentar usar um jogador que já recebeu amarelo
      let player = "";
      const yellowCardPlayers = cards
        .filter(card => card.away_fault && card.card === "yellow card")
        .map(card => card.away_fault);

      if (isSecondYellow && yellowCardPlayers.length > 0) {
        // Usar um jogador que já recebeu amarelo
        player = yellowCardPlayers[Math.floor(Math.random() * yellowCardPlayers.length)];
      } else {
        // Usar um jogador aleatório
        player = getRandomAwayPlayer();
      }

      cards.push({
        time: minute.toString(),
        away_fault: player,
        card: isSecondYellow ? "yellow card/red card" : "red card",
        home_fault: "",
        info: isSecondYellow ? "Segundo cartão amarelo" : "Falta grave"
      });
    }

    // Ordenar eventos por minuto
    goalscorers.sort((a, b) => parseInt(a.time) - parseInt(b.time));
    cards.sort((a, b) => parseInt(a.time) - parseInt(b.time));

    // Gerar substituições
    const homeSubstitutions = [];
    const awaySubstitutions = [];

    // Número de substituições (3-5 por time)
    const homeSubsCount = Math.floor(3 + Math.random() * 3);
    const awaySubsCount = Math.floor(3 + Math.random() * 3);

    // Gerar substituições para o time da casa
    for (let i = 0; i < homeSubsCount; i++) {
      // Substituições tendem a ocorrer no segundo tempo
      let minute;
      if (i === 0 && Math.random() < 0.3) {
        // 30% de chance da primeira substituição ser no intervalo
        minute = 46;
      } else if (i === 0) {
        // Primeira substituição geralmente entre 55-70
        minute = Math.floor(55 + Math.random() * 15);
      } else if (i === 1) {
        // Segunda substituição geralmente entre 65-80
        minute = Math.floor(65 + Math.random() * 15);
      } else {
        // Demais substituições geralmente entre 75-90
        minute = Math.floor(75 + Math.random() * 15);
      }

      homeSubstitutions.push({
        time: minute.toString(),
        substitution: "on",
        substitution_player: getRandomHomePlayer()
      });

      homeSubstitutions.push({
        time: minute.toString(),
        substitution: "off",
        substitution_player: getRandomHomePlayer()
      });
    }

    // Gerar substituições para o time visitante
    for (let i = 0; i < awaySubsCount; i++) {
      // Substituições tendem a ocorrer no segundo tempo
      let minute;
      if (i === 0 && Math.random() < 0.3) {
        // 30% de chance da primeira substituição ser no intervalo
        minute = 46;
      } else if (i === 0) {
        // Primeira substituição geralmente entre 55-70
        minute = Math.floor(55 + Math.random() * 15);
      } else if (i === 1) {
        // Segunda substituição geralmente entre 65-80
        minute = Math.floor(65 + Math.random() * 15);
      } else {
        // Demais substituições geralmente entre 75-90
        minute = Math.floor(75 + Math.random() * 15);
      }

      awaySubstitutions.push({
        time: minute.toString(),
        substitution: "on",
        substitution_player: getRandomAwayPlayer()
      });

      awaySubstitutions.push({
        time: minute.toString(),
        substitution: "off",
        substitution_player: getRandomAwayPlayer()
      });
    }

    // Ordenar substituições por minuto
    homeSubstitutions.sort((a, b) => parseInt(a.time) - parseInt(b.time));
    awaySubstitutions.sort((a, b) => parseInt(a.time) - parseInt(b.time));

    // Dados simulados para desenvolvimento
    return {
      goalscorer: goalscorers,
      cards: cards,
      substitutions: {
        home: homeSubstitutions,
        away: awaySubstitutions
      }
    };
  }
};

// Função para buscar predições da partida
export const getMatchPredictions = async (matchId: string, isAuthenticated: boolean) => {
  try {
    const apiClient = isAuthenticated ? api : apiPublic;
    const response = await apiClient.get(`/football/getPredictions/${matchId}`);
    return response.data.body?.data || response.data?.data || [];
  } catch (error) {
    console.error('Erro ao buscar predições:', error);

    // Dados simulados para desenvolvimento
    return [{
      match_id: matchId,
      match_hometeam_id: "",
      match_hometeam_name: "",
      match_awayteam_name: "",
      match_awayteam_id: "",
      prob_HW: "45",
      prob_D: "25",
      prob_AW: "30",
      prob_HW_D: "70",
      prob_AW_D: "55",
      prob_bts: "68",
      prob_O: "75",
      prob_U: "25",
      prob_O1: "85",
      prob_U1: "15",
      prob_O3: "32",
      prob_U3: "68"
    }];
  }
};

// Função para buscar histórico de confrontos diretos
export const getHeadToHead = async (team1Id: string, team2Id: string, isAuthenticated: boolean) => {
  try {
    const apiClient = isAuthenticated ? api : apiPublic;
    const response = await apiClient.get(`/football/getH2H/${team1Id}/${team2Id}`);
    return response.data.body?.data || response.data?.data || [];
  } catch (error) {
    console.error('Erro ao buscar confrontos diretos:', error);
    throw error;
  }
};

export default apiClient;